import React, { useState, useEffect, useCallback } from 'react';
import DynamicBackground from '../../components/DynamicBackground';
import CreditsDisplay from '../../components/CreditsDisplay';
import ExpertContactMessage from '../../components/ExpertContactMessage';
import '../../styles/darkMode.css';
import './ContractGenerationDashboard.css';
import { getUserGenerations, getGenerationById, deleteGeneration } from '../../services/contract-generation.service';
import { RecordDealGeneration } from '../../types/api.types';
import { useUser } from '../../contexts/UserContext';
import { API_BASE_URL } from '../../config';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const PAGE_SIZE = 10;

const ContractGenerationDashboard: React.FC = () => {
  const { user } = useUser();
  const [generations, setGenerations] = useState<RecordDealGeneration[]>([]);
  const [selectedGeneration, setSelectedGeneration] = useState<RecordDealGeneration | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [highlightRecent, setHighlightRecent] = useState(false);

  const loadGenerations = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: 0,
      };
      
      const data = await getUserGenerations(params);
      setGenerations(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load generations');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const loadMore = useCallback(async () => {
    if (!user?.id || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: generations.length,
      };

      const data = await getUserGenerations(params);
      setGenerations(prev => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more generations');
    } finally {
      setIsLoadingMore(false);
    }
  }, [user?.id, generations.length, isLoadingMore]);

  useEffect(() => {
    if (user?.id) {
      loadGenerations();
    }
  }, [user?.id, loadGenerations]);

  useEffect(() => {
    // Check if we should highlight the recent generation
    const urlParams = new URLSearchParams(window.location.search);
    const shouldHighlight = urlParams.get('highlight') === 'recent';
    setHighlightRecent(shouldHighlight);

  }, []);

  const handleGenerationClick = async (generation: RecordDealGeneration) => {
    try {
      const detailedGeneration = await getGenerationById(generation.id);
      setSelectedGeneration(detailedGeneration);
      setShowDetailModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load generation details');
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Prevent card click
    
    if (window.confirm('Are you sure you want to delete this contract generation? This action cannot be undone.')) {
      try {
        await deleteGeneration(id);
        // Update state to remove the deleted item
        setGenerations(prev => prev.filter(g => g.id !== id));
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to delete generation');
      }
    }
  };

  const renderAgreementDetails = (agreement: any) => {
    return (
      <div className="agreement-details">
        <h4>Agreement Details</h4>
        <div className="agreement-grid">
          <div className="agreement-section">
            <h5>Basic Information</h5>
            <p><strong>Agreement Date:</strong> {agreement.agreementDate}</p>
            <p><strong>Relationship Type:</strong> {agreement.relationshipType}</p>
            <p><strong>License/Assignment:</strong> {agreement.licenseOrAssignment}</p>
            <p><strong>Territory:</strong> {agreement.territory}</p>
            <p><strong>Term:</strong> {agreement.termYears} years</p>
            <p><strong>Sell-off Period:</strong> {agreement.sellOffMonths} months</p>
          </div>
          
          <div className="agreement-section">
            <h5>Label Information</h5>
            <p><strong>Name:</strong> {agreement.label?.name}</p>
            <p><strong>Jurisdiction:</strong> {agreement.label?.jurisdiction}</p>
            <p><strong>Address:</strong> {agreement.label?.address}</p>
          </div>
          
          <div className="agreement-section">
            <h5>Artist Information</h5>
            <p><strong>Name:</strong> {agreement.artist?.name}</p>
            <p><strong>Address:</strong> {agreement.artist?.address}</p>
          </div>
          
          <div className="agreement-section">
            <h5>Royalty Splits</h5>
            <p><strong>Sales & Streaming:</strong> {agreement.royaltySplits?.salesStreams}</p>
            <p><strong>Sync:</strong> {agreement.royaltySplits?.sync}</p>
            <p><strong>Remixes:</strong> {agreement.royaltySplits?.remixes}</p>
          </div>
          
          {agreement.trackList && agreement.trackList.length > 0 && (
            <div className="agreement-section">
              <h5>Track List</h5>
              <ul>
                {agreement.trackList.map((track: any, index: number) => (
                  <li key={index}>
                    <strong>{track.title}</strong> - ISRC: {track.isrc}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="agreement-section">
            <h5>Additional Terms</h5>
            <p><strong>Optional Future Works:</strong> {agreement.optionalFutureWorks ? 'Yes' : 'No'}</p>
            <p><strong>Dispute Jurisdiction:</strong> {agreement.disputeJurisdiction}</p>
            {agreement.recoupableCosts && agreement.recoupableCosts.length > 0 && (
              <p><strong>Recoupable Costs:</strong> {agreement.recoupableCosts.join(', ')}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGenerationDetail = (generation: RecordDealGeneration) => {
    return (
      <div className="generation-detail">
        <div className="generation-header">
          <h3>{generation.fileName}</h3>
          <div className="generation-meta">
            <span className="generation-type">Record Deal</span>
            <span className="generation-date">
              {new Date(generation.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="generation-actions">
          <button 
            className="dark-button primary"
            onClick={() => downloadPDF(generation.id)}
          >
            Download as PDF
          </button>
        </div>

        {renderAgreementDetails(generation.agreement)}

        <div className="generated-content-section">
          <h4>Generated Content</h4>
          <div className="generated-content">
            <pre className="markdown-content">{generation.generatedContent}</pre>
          </div>
        </div>
      </div>
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getContractTypeLabel = (agreement: any): string => {
    // For now, we only have record deals, but this can be expanded
    return 'Record Deal';
  };

  const generatePdfWithJsPDF = async (htmlContent: string) => {
    try {
      // Create a temporary container to render the HTML
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = htmlContent;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '210mm'; // A4 width
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      tempContainer.style.fontSize = '12px';
      tempContainer.style.lineHeight = '1.4';
      document.body.appendChild(tempContainer);

      // Convert HTML to canvas
      const canvas = await html2canvas(tempContainer, {
        useCORS: true,
        allowTaint: true,
        background: '#ffffff',
        width: tempContainer.scrollWidth,
        height: tempContainer.scrollHeight,
      });

      // Remove temporary container
      document.body.removeChild(tempContainer);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      pdf.save('agreement.pdf');
      
    } catch (error) {
      console.error('jsPDF generation failed:', error);
      setError('Failed to generate PDF');
    }
  };

  const downloadPDF = async (recordDealId: string) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.idToken) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/api/write/record-deal-generations/${recordDealId}/html`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch HTML content');
      }

      const htmlContent = await response.text();
      await generatePdfWithJsPDF(htmlContent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download PDF');
    }
  };

  return (
    <DynamicBackground>
      <div className="dark-container">
        <div className="dashboard-header">
          <CreditsDisplay variant="header" />
          <h2 className="dark-title">Contract Generation History</h2>
          <p className="dark-subtitle">View and manage your past contract generations</p>
        </div>

        {isLoading ? (
          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p className="dark-text">Loading your generations...</p>
          </div>
        ) : error ? (
          <div className="error-section">
            <p className="dark-text error-text">{error}</p>
            <button 
              className="dark-button primary"
              onClick={loadGenerations}
            >
              Try Again
            </button>
          </div>
        ) : generations.length === 0 ? (
          <div className="empty-section">
            <p className="dark-text">No contract generations found. Start by creating a contract!</p>
          </div>
        ) : (
          <>
            <div className="generations-grid">
              {generations.map((generation, index) => (
                <div 
                  key={generation.id} 
                  className={`generation-card dark-card ${highlightRecent && index === 0 ? 'highlighted-recent' : ''}`}
                  onClick={() => handleGenerationClick(generation)}
                >
                  <button 
                    className="delete-button"
                    onClick={(e) => handleDelete(e, generation.id)}
                    title="Delete generation"
                  >
                    ×
                  </button>
                  <div className="card-header">
                    <h3 className="dark-text">{generation.fileName}</h3>
                    <div className="badge-container">
                      {highlightRecent && index === 0 && (
                        <span className="new-badge">NEW</span>
                      )}
                      <span className="generation-type-badge record-deal">
                        {getContractTypeLabel(generation.agreement)}
                      </span>
                    </div>
                  </div>
                  <div className="card-meta">
                    <span className="file-size">{formatFileSize(generation.fileSize)}</span>
                    <span className="generation-date">
                      {new Date(generation.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="card-preview">
                    <p className="dark-text-muted">
                      <strong>Label:</strong> {generation.agreement?.label?.name}
                    </p>
                    <p className="dark-text-muted">
                      <strong>Artist:</strong> {generation.agreement?.artist?.name}
                    </p>
                    <p className="dark-text-muted">
                      <strong>Term:</strong> {generation.agreement?.termYears} years
                    </p>
                    <p className="dark-text-muted">
                      Click to view detailed contract...
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <button
                  className="dark-button primary"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedGeneration && (
          <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="dark-text">Contract Details</h3>
                <button 
                  className="modal-close"
                  onClick={() => setShowDetailModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {renderGenerationDetail(selectedGeneration)}
              </div>
            </div>
          </div>
        )}

        <ExpertContactMessage />
      </div>
    </DynamicBackground>
  );
};

export default ContractGenerationDashboard; 